import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { topics } from '@/mocks/topics';
import { useSeo } from '@/hooks/useSeo';

export default function TopicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const topic = topics.find((t) => t.slug === slug);
  const related = topics.filter((t) => t.slug !== slug && t.category === topic?.category).slice(0, 2);
  const fallbackRelated = topics.filter((t) => t.slug !== slug).slice(0, 2);
  const relatedList = (related.length ? related : fallbackRelated);

  const url = `https://jinpenge.cn/topic/${slug}`;

  useSeo({
    title: topic ? `${topic.title} - 黑料社网${topic.categoryName}专题合集` : '专题未找到 - 黑料社网',
    description: topic
      ? `${topic.summary} 黑料社网${topic.categoryName}专题合集，共收录 ${topic.count} 条，最近更新于 ${topic.updated}。`
      : '该专题暂未上线，返回黑料社网专题合集总览浏览其他合集。',
    keywords: topic
      ? `${topic.tags.join(',')},黑料社网专题,${topic.categoryName},合集盘点`
      : '黑料社网,专题合集',
    canonicalPath: `/topic/${slug}`,
    jsonLd: topic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: topic.title,
            description: topic.summary,
            url,
            articleSection: topic.categoryName,
            keywords: topic.tags.join(','),
            dateModified: topic.updated,
            author: { '@type': 'Organization', name: '黑料社网' },
            publisher: { '@type': 'Organization', name: '黑料社网' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jinpenge.cn/' },
              { '@type': 'ListItem', position: 2, name: '专题合集', item: 'https://jinpenge.cn/topics' },
              { '@type': 'ListItem', position: 3, name: topic.title, item: url },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <div className="px-4 md:px-10 py-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
        <h1 className="mt-5 font-heading text-2xl md:text-3xl font-black text-foreground-950">这个专题还没上线</h1>
        <p className="mt-3 text-sm text-foreground-600">它可能已经合并到其他合集，或者正在编辑中。</p>
        <Link
          to="/topics"
          className="mt-7 inline-flex items-center gap-2 h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回专题合集
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 md:px-10 pt-8">
        <Breadcrumb
          items={[{ label: '首页', path: '/' }, { label: '专题合集', path: '/topics' }, { label: topic.title }]}
        />
      </div>

      <header className="px-4 md:px-10 pt-6 pb-8">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/topics?cat=${topic.category}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer"
          >
            <i className="ri-price-tag-3-line"></i>
            {topic.categoryName}
          </Link>
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className="ri-time-line"></i>
            最近更新 {topic.updated}
          </span>
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className="ri-stack-line"></i>
            {topic.count} 条
          </span>
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className="ri-book-read-line"></i>
            约 {topic.readMins} 分钟
          </span>
          <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px]">{topic.status}</span>
        </div>

        <h1 className="mt-5 font-heading text-2xl md:text-4xl lg:text-5xl font-black text-foreground-950 leading-snug max-w-4xl">
          {topic.title}
        </h1>
        <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">{topic.intro}</p>
      </header>

      <div className="px-4 md:px-10">
        <div className="relative w-full h-60 md:h-96 rounded-lg overflow-hidden">
          <img
            src={topic.cover}
            alt={`${topic.title} - 黑料社网${topic.categoryName}专题合集封面`}
            title={`${topic.title} 专题合集`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      <div className="px-4 md:px-10 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        <aside className="lg:col-span-3 lg:sticky lg:top-32">
          <h2 className="font-heading text-base font-bold text-foreground-950">本页目录</h2>
          <nav className="mt-4 border-l border-background-200" aria-label="内容目录">
            <ul className="space-y-1">
              {topic.sections.map((s, idx) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="block pl-4 py-2 text-sm text-foreground-600 hover:text-primary-600 hover:border-l-2 hover:border-primary-500 -ml-px transition-colors cursor-pointer"
                  >
                    <span className="text-primary-500 font-heading mr-2">{String(idx + 1).padStart(2, '0')}</span>
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div className="mt-7 rounded-lg bg-background-100 border border-background-200 p-5">
            <h3 className="font-heading text-sm font-bold text-foreground-950">标签</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {topic.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full text-[11px] bg-secondary-100 text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </aside>

        <article className="lg:col-span-9 max-w-3xl">
          {topic.sections.map((s, idx) => (
            <section key={s.id} id={s.id} className="scroll-mt-32 mb-12 last:mb-0">
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 flex items-start gap-3">
                <span className="text-primary-500">{String(idx + 1).padStart(2, '0')}</span>
                {s.heading}
              </h2>
              <div className="mt-4 space-y-4">
                {s.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base leading-loose text-foreground-700">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}

          <div className="mt-12 pt-8 border-t border-background-200 rounded-lg bg-accent-50 p-6">
            <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-lightbulb-flash-line text-accent-700"></i>
              想推荐新条目？
            </h3>
            <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
              如果你觉得这个专题还漏掉了值得收录的内容，欢迎写信到
              <a href="mailto:hello@heiliaoshe.example.com" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                {' '}hello@heiliaoshe.example.com{' '}
              </a>
              ，注明专题名称与你推荐的理由即可。
            </p>
          </div>
        </article>
      </div>

      <section className="px-4 md:px-10 pb-16" aria-label="相关专题推荐">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-heading text-xl md:text-3xl font-black text-foreground-950">相关专题推荐</h2>
          <Link
            to="/topics"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {relatedList.map((t) => (
            <TopicCard key={t.id} topic={t} />
          ))}
        </div>
      </section>
    </div>
  );
}